import React, { useState } from 'react';
import { User, Mail, Trash2, CreditCard, AlertCircle, ArrowLeft, X } from 'lucide-react';
import { updateUserInfo, cancelStripeSubscription } from '../../../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';
import { cancelWhopSubscription } from '../../../apiLayer/dashBoardAPIs/whopAPIs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { cn } from '../../../lib/utils';

const SettingRow = ({ label, description, children, className }) => (
  <div className={cn("flex flex-col sm:flex-row sm:items-center justify-between py-6 border-b border-white/5 last:border-0 gap-4", className)}>
    <div className="space-y-1 flex-1">
      <div className="text-sm font-medium text-white">{label}</div>
      <div className="text-xs text-muted-foreground">{description}</div>
    </div>
    <div className="flex items-center gap-2">
      {children}
    </div>
  </div>
);

const Settings = ({ userInfo, onTabChange, subscriptions, paymentInfo }) => {
  const [fullName, setFullName] = useState(userInfo?.fullname || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // View state: 'settings' or 'subscription'
  const [currentView, setCurrentView] = useState('settings');

  // Cancellation modal states
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelFormData, setCancelFormData] = useState({
    reason: '',
    feedback: ''
  });

  // Lifetime owners have no subscription object — users_plan_name (from
  // /payment/get_user_info, merged into userInfo by the dashboard hook) is the
  // source of truth. Without this they'd render as free-trial users below.
  const isLifetime = userInfo?.users_plan_name === 'lifetime';

  // Get subscription details from unified payment data
  const getSubscriptionDetails = () => {
    if (isLifetime) {
      return {
        plan_name: 'Lifetime',
        price: 'Paid once',
        billing_cycle: 'one-time',
        status: 'lifetime',
        next_billing_date: 'N/A',
        cancel_at_period_end: false,
        canceled_at: null,
        current_period_end: null,
        cancellation_effective_date: null
      };
    }

    if (!subscriptions?.active_subscription?.plans?.length) {
      return {
        plan_name: 'Free Plan',
        price: '$0.00',
        billing_cycle: 'N/A',
        status: 'inactive',
        next_billing_date: 'N/A',
        cancel_at_period_end: false,
        canceled_at: null,
        current_period_end: null,
        cancellation_effective_date: null
      };
    }

    const plan = subscriptions.active_subscription.plans[0];
    const sub = subscriptions.active_subscription;

    let status;
    if (sub.status === 'active' && sub.cancel_at_period_end) {
      status = 'cancelling';
    } else if (sub.status === 'active') {
      status = 'active';
    } else if (sub.status === 'canceled' && subscriptions.is_subscriber) {
      status = 'cancelled';
    } else {
      status = 'inactive';
    }

    return {
      plan_name: plan.plan_name,
      price: `$${(plan.amount / 100).toFixed(2)}`,
      billing_cycle: plan.interval,
      status,
      next_billing_date: subscriptions.next_billing_date
        ? new Date(subscriptions.next_billing_date * 1000).toLocaleDateString()
        : 'N/A',
      cancel_at_period_end: sub.cancel_at_period_end,
      canceled_at: sub.canceled_at,
      current_period_end: sub.current_period_end,
      cancellation_effective_date: subscriptions.cancellation_effective_date
    };
  };

  const userSubscription = getSubscriptionDetails();
  const hasActiveSubscription = subscriptions?.is_subscriber;
  const canCancel = subscriptions?.is_subscriber &&
    subscriptions?.active_subscription?.status === 'active' &&
    !subscriptions?.active_subscription?.cancel_at_period_end;

  const handleUpdateProfile = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await updateUserInfo({ fullname: fullName });
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = () => {
    setCurrentView('subscription');
  };

  const handleBackToSettings = () => {
    setCurrentView('settings');
    setShowCancelForm(false);
    setShowConfirmation(false);
  };

  const handleCancelSubscription = () => {
    setShowCancelForm(true);
  };

  const handleCancelFormChange = (field, value) => {
    setCancelFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelFormSubmit = async () => {
    if (!cancelFormData.reason || !cancelFormData.feedback) return;

    setCancelLoading(true);
    try {
      // Real cancellation via the provider's endpoint — no email.
      const provider = subscriptions?.provider || 'stripe';
      if (provider === 'whop') {
        await cancelWhopSubscription();
      } else {
        const plan = subscriptions?.active_subscription?.plans?.[0]?.plan_name;
        await cancelStripeSubscription(plan);
      }
      setShowCancelForm(false);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(error?.response?.data?.detail || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setCancelFormData({ reason: '', feedback: '' });
    setCurrentView('settings');
    // Reload so the dashboard reflects the cancelled/now-cancelling subscription.
    window.location.reload();
  };

  // Subscription Management View
  if (currentView === 'subscription') {
    return (
      <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
        <button
          onClick={handleBackToSettings}
          className="flex items-center gap-2 text-primary hover:text-primary/80 mb-6 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Manage Subscription</h1>
          <p className="text-sm text-muted-foreground">View and manage your current subscription plan</p>
        </div>

        <div className="space-y-6 max-w-4xl">
          <Card className="bg-[#121219]/80 border-white/10">
            <CardHeader>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>Your active plan details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{userSubscription.plan_name}</h3>
                    <div className="text-3xl font-bold text-primary">
                      {userSubscription.price}
                      <span className="text-sm font-normal text-muted-foreground">
                        {isLifetime ? ' once' : `/${userSubscription.billing_cycle}`}
                      </span>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      userSubscription.status === 'lifetime'
                        ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                        : userSubscription.status === 'active'
                          ? "bg-green-500/10 text-green-500 border-green-500/30"
                          : userSubscription.status === 'cancelling'
                            ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                            : userSubscription.status === 'cancelled'
                              ? "bg-red-500/10 text-red-500 border-red-500/30"
                              : "bg-gray-500/10 text-gray-500 border-gray-500/30"
                    )}
                  >
                    {userSubscription.status === 'lifetime' ? 'Lifetime'
                      : userSubscription.status === 'active' ? 'Active'
                      : userSubscription.status === 'cancelling' ? 'Cancelling'
                      : userSubscription.status === 'cancelled' ? 'Cancelled'
                      : 'Inactive'}
                  </Badge>
                </div>

                {userSubscription.status === 'active' && userSubscription.next_billing_date !== 'N/A' && (
                  <p className="text-sm text-muted-foreground mb-6">
                    Next billing date: <span className="text-white">{userSubscription.next_billing_date}</span>
                  </p>
                )}

                {userSubscription.status === 'cancelling' && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-6">
                    <p className="text-sm text-yellow-400">
                      Your subscription has been cancelled and will end on{' '}
                      <span className="font-medium text-white">
                        {userSubscription.cancellation_effective_date
                          ? new Date(userSubscription.cancellation_effective_date * 1000).toLocaleDateString()
                          : userSubscription.next_billing_date}
                      </span>.
                      You will continue to have full access until then.
                    </p>
                  </div>
                )}

                {userSubscription.status === 'cancelled' && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
                    <p className="text-sm text-red-400">
                      Your subscription has been cancelled. You have access until{' '}
                      <span className="font-medium text-white">
                        {userSubscription.current_period_end
                          ? new Date(userSubscription.current_period_end * 1000).toLocaleDateString()
                          : 'N/A'}
                      </span>.
                    </p>
                  </div>
                )}

                {canCancel && (
                  <Button
                    variant="destructive"
                    onClick={handleCancelSubscription}
                    className="bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/20"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cancellation Form Modal */}
        {showCancelForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg bg-[#121219] border border-white/10 rounded-xl shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-xl font-bold text-white">Cancel Subscription</h2>
                <button
                  onClick={() => setShowCancelForm(false)}
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <p className="text-muted-foreground text-sm">
                  We're sorry to see you go! Please help us improve by telling us why you're cancelling your subscription.
                </p>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Reason for cancellation *</label>
                  <select
                    value={cancelFormData.reason}
                    onChange={(e) => handleCancelFormChange('reason', e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select a reason</option>
                    <option value="too_expensive">Too expensive</option>
                    <option value="not_using">Not using the service enough</option>
                    <option value="missing_features">Missing features I need</option>
                    <option value="technical_issues">Technical issues</option>
                    <option value="switching_service">Switching to another service</option>
                    <option value="temporary_break">Taking a temporary break</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Additional feedback *</label>
                  <textarea
                    value={cancelFormData.feedback}
                    onChange={(e) => handleCancelFormChange('feedback', e.target.value)}
                    placeholder="Please provide any additional feedback that could help us improve our service..."
                    className="w-full h-32 bg-black/40 border border-white/10 rounded-lg p-3 text-white text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowCancelForm(false)}>
                    Keep Subscription
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleCancelFormSubmit}
                    disabled={!cancelFormData.reason || !cancelFormData.feedback || cancelLoading}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {cancelLoading ? 'Processing...' : 'Confirm Cancellation'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-md bg-[#121219] border border-white/10 rounded-xl shadow-2xl p-6 text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">Cancellation Request Submitted</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Your cancellation request has been submitted. You will continue to have access to your current plan until the end of your billing period.
                <br /><br />
                Thank you for your feedback. We'll use it to improve our service.
              </p>
              <Button onClick={handleCloseConfirmation} className="w-full">
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main Settings View
  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account preferences and settings</p>
      </div>

      <div className="space-y-6 max-w-4xl">
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>Manage your public profile information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-0">
            <SettingRow
              label="Full Name"
              description="Your name as it appears on your profile"
            >
              <div className="flex items-center gap-2">
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-black/20 border-white/10 text-white w-[200px]"
                />
                <Button onClick={handleUpdateProfile} disabled={loading} size="sm">
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </SettingRow>

            <SettingRow
              label="Email Address"
              description="The email address associated with your account"
            >
              <div className="flex items-center gap-2">
                <Input
                  value={userInfo?.email || ''}
                  disabled
                  className="bg-white/5 border-white/5 text-muted-foreground w-[200px] cursor-not-allowed"
                />
                <Badge variant="outline" className="border-green-500/30 text-green-500 bg-green-500/10 h-9">
                  Verified
                </Badge>
              </div>
            </SettingRow>

            {message.text && (
              <div className={cn(
                "mt-4 p-3 rounded-lg text-sm",
                message.type === 'success' ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
              )}>
                {message.text}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Management */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Billing & Subscription
            </CardTitle>
            <CardDescription>Manage your subscription and billing preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Current Plan"
              description={
                userSubscription.status === 'lifetime'
                  ? 'Lifetime — paid once, permanent access'
                  : userSubscription.status === 'cancelling'
                    ? `${userSubscription.plan_name} - Cancelling at period end`
                    : userSubscription.status === 'cancelled'
                      ? `${userSubscription.plan_name} - Cancelled`
                      : userSubscription.status === 'active'
                        ? `${userSubscription.plan_name} - ${userSubscription.price}/${userSubscription.billing_cycle}`
                        : 'No active subscription'
              }
            >
              <Button variant="outline" onClick={handleManageSubscription} className="gap-2">
                Manage Subscription
              </Button>
            </SettingRow>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-[#121219]/80 border-red-500/20">
          <CardHeader>
            <CardTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Danger Zone
            </CardTitle>
            <CardDescription>Irreversible account actions</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Delete Account"
              description="Permanently delete your account and all usage data"
            >
              <Button
                variant="destructive"
                onClick={() => onTabChange?.('contact')}
                className="gap-2 bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/20"
              >
                <Trash2 className="w-4 h-4" /> Contact Support
              </Button>
            </SettingRow>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
